import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button as CrcButton } from '@crc-evans/ui-components';
import './GenerateMobilistModal.css';

// ── Field component ──────────────────────────────────
const Field = ({ label, required, error, children }) => (
  <div className="gm-field">
    <label className={`gm-label${required ? ' gm-label-required' : ''}`}>{label}</label>
    {children}
    {error && <span className="gm-error">{error}</span>}
  </div>
);

// ── Main Component ───────────────────────────────────
const MasterDataModal = ({ visible, onCancel, onSave, initialData }) => {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (visible) {
      if (initialData) {
        setValues(initialData);
      } else {
        setValues({});
      }
      setErrors({});
    }
  }, [visible, initialData]);

  const handleChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = () => {
    // Validate required fields
    if (!values['£ KEY']) {
      setErrors({ '£ KEY': 'Description is required' });
      return;
    }
    
    // Pass values back
    onSave(values);
  };

  // Close on overlay click
  const overlayRef = useRef(null);
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onCancel();
    }
  };

  if (!visible) return null;

  const modal = (
    <div className="gm-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="gm-box" style={{ width: '700px' }}>
        
        {/* Header */}
        <div className="gm-header">
          <h2 className="gm-title">{initialData ? 'Edit Master Data' : 'Add Master Data'}</h2>
          <button className="gm-close" onClick={onCancel} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className="gm-body">
          <div className="gm-row gm-row-2">
            <div style={{ gridColumn: '1 / span 2' }}>
              <Field label="Description" required error={errors['£ KEY']}>
                <input
                  className="gm-input"
                  placeholder="e.g., 1060CFM Compressor"
                  value={values['£ KEY'] || ''}
                  onChange={(e) => handleChange('£ KEY', e.target.value)}
                />
              </Field>
            </div>
          </div>

          <div className="gm-row gm-row-2">
            <Field label="SAP #">
              <input
                className="gm-input"
                placeholder="Enter SAP Number"
                value={values['__EMPTY'] || ''}
                onChange={(e) => handleChange('__EMPTY', e.target.value)}
              />
            </Field>
            <Field label="Moblist Cost £">
              <input
                className="gm-input gm-input-number"
                type="number"
                step="0.01"
                placeholder="e.g., 1500.00"
                value={values['__EMPTY_1'] || ''}
                onChange={(e) => handleChange('__EMPTY_1', e.target.value)}
              />
            </Field>
          </div>

          <div className="gm-row gm-row-2">
            <Field label="HS #">
              <input
                className="gm-input"
                placeholder="Enter HS Number"
                value={values['__EMPTY_2'] || ''}
                onChange={(e) => handleChange('__EMPTY_2', e.target.value)}
              />
            </Field>
            <Field label="WBS #">
              <input
                className="gm-input"
                placeholder="Enter WBS Number"
                value={values['__EMPTY_3'] || ''}
                onChange={(e) => handleChange('__EMPTY_3', e.target.value)}
              />
            </Field>
          </div>

          <div className="gm-row gm-row-2">
            <Field label="Ret/Con">
              <input
                className="gm-input"
                placeholder="e.g., RET or CON"
                value={values['__EMPTY_4'] || ''}
                onChange={(e) => handleChange('__EMPTY_4', e.target.value)}
              />
            </Field>
            <Field label="Type">
              <input
                className="gm-input"
                placeholder="e.g., CCAB"
                value={values['__EMPTY_5'] || ''}
                onChange={(e) => handleChange('__EMPTY_5', e.target.value)}
              />
            </Field>
          </div>

          <div className="gm-row gm-row-2">
            <Field label="Get Ready -R (£)">
              <input
                className="gm-input gm-input-number"
                type="number"
                step="0.01"
                placeholder="e.g., 0.00"
                value={values['__EMPTY_6'] || ''}
                onChange={(e) => handleChange('__EMPTY_6', e.target.value)}
              />
            </Field>
            <Field label="Purchase -R (£)">
              <input
                className="gm-input gm-input-number"
                type="number"
                step="0.01"
                placeholder="e.g., 0.00"
                value={values['__EMPTY_7'] || ''}
                onChange={(e) => handleChange('__EMPTY_7', e.target.value)}
              />
            </Field>
          </div>

          <div className="gm-row gm-row-2">
            <Field label="Owner">
              <input
                className="gm-input"
                placeholder="e.g., Supply Chain"
                value={values['__EMPTY_8'] || ''}
                onChange={(e) => handleChange('__EMPTY_8', e.target.value)}
              />
            </Field>
            <Field label="Category">
              <input
                className="gm-input"
                placeholder="e.g., Cat. 3"
                value={values['__EMPTY_9'] || ''}
                onChange={(e) => handleChange('__EMPTY_9', e.target.value)}
              />
            </Field>
          </div>

          <div className="gm-row gm-row-2">
            <div style={{ gridColumn: '1 / span 2' }}>
              <Field label="Ext. Description">
                <input
                  className="gm-input"
                  placeholder="Enter extended description"
                  value={values['__EMPTY_10'] || ''}
                  onChange={(e) => handleChange('__EMPTY_10', e.target.value)}
                />
              </Field>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="gm-footer">
          <CrcButton label="Cancel" variant="secondary" onClick={onCancel} size="small" />
          <CrcButton label="Save" variant="primary" onClick={handleSave} size="small" />
        </div>

      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default MasterDataModal;
